import type { AutofacetState } from 'reducers/autofacet';
import type { BreadcrumbType } from 'types/search';

/**
 * Extracts the facet group from a breadcrumb's removeName.
 * Format: "Remove facetField: facetValue" or "Remove facetField"
 * @example extractFacetGroup("Remove brandNameFacet: Nike") // "brandNameFacet"
 */
export function extractFacetGroup(removeName: string): string {
  return removeName?.split(' ')[1]?.replace(/:/g, '') || '';
}

/**
 * Determines if enableTermAutoFaceting should be sent to the API.
 * Returns false when Brand/Department filters were removed.
 * This prevents the backend from re-applying filters the user removed.
 */
export function shouldEnableTermAutoFaceting(state: AutofacetState): boolean {
  return !state.hasRemovedOtherAutofacet;
}

/**
 * Determines if disableTermAutoFaceting should be sent to the API.
 * Returns true when Gender filters were removed.
 * This tells the backend to stop auto-faceting for this search session.
 */
export function shouldDisableTermAutoFaceting(state: AutofacetState): boolean {
  return state.hasRemovedGender;
}

/**
 * Gets the API parameters to include in search requests based on autofacet state.
 * Returns array of parameter names like ['enableTermAutoFaceting', 'disableTermAutoFaceting'].
 */
export function getAutofacetApiParams(state: AutofacetState): string[] {
  const params: string[] = [];

  if (shouldEnableTermAutoFaceting(state)) {
    params.push('enableTermAutoFaceting');
  }

  if (shouldDisableTermAutoFaceting(state)) {
    params.push('disableTermAutoFaceting');
  }

  return params;
}

/**
 * Finds the breadcrumb for a specific facet value.
 * Used when deselecting a known value.
 * @example findMatchingBreadcrumb('brandNameFacet', 'Nike', breadcrumbs) // breadcrumb for "Nike" in brand
 * @example findMatchingBreadcrumb('zc1', 'Clothing', breadcrumbs) // undefined if only "Shoes" exists
 */
export function findMatchingBreadcrumb(facetField: string, facetValueName: string, breadcrumbs: BreadcrumbType[]): BreadcrumbType | undefined {
  return breadcrumbs.find(
    breadcrumb => extractFacetGroup(breadcrumb.removeName) === facetField && breadcrumb.name.toLowerCase() === facetValueName.toLowerCase()
  );
}

/**
 * Finds any breadcrumb in a facet group, regardless of value.
 * Used when selecting a new value to check if the replaced value was autofaceted.
 * @example findBreadcrumbByGroup('zc1', breadcrumbs) // breadcrumb for "Shoes" even though user selected "Clothing"
 */
export function findBreadcrumbByGroup(facetField: string, breadcrumbs: BreadcrumbType[]): BreadcrumbType | undefined {
  return breadcrumbs.find(breadcrumb => extractFacetGroup(breadcrumb.removeName) === facetField);
}
